import { ref, computed, watchEffect, reactive } from "vue";

// Utils
import { differenceInDays } from "date-fns";
import { openSupportPrefill } from "@helpers/supportChat";
import { openHelpArticle } from "@helpers/helpCenter";

// Store
import { defineStore, storeToRefs } from "pinia";
import {
  useIntegrationsStore,
  useAccountStore,
  useSendersStore,
  useUserStore,
  useSessionStore,
  useContactsStore,
  useUserActivityStore,
  useInvoicePaymentsStore,
  useAlertStore,
} from "@store";

// Application
import { useRouterApp, useUserActivityApplication } from "@application";

// Services
import { useTrackingEvents } from "@/vue/composables/trackingevents";

// Domain
import {
  Ecommerce,
  ecommerceHasAbandonedCart,
  ecommerceHasRemarketing,
  ecommerceHasPendingPayments,
  ecommerceHasOptinSupport,
  isEcommerceId,
  isSupportedEcommerce,
} from "@domain/ecommerce";

import type { Rewards, RewardId, TaskIds, Tasks } from "@domain/onboarding";
import { getTaskIdFromEventAction, getEventActionFromTaskId } from "@domain/onboarding";

import {
  getTaskIdFromEvent,
  isOmittedEvent,
  isRewardClaimedEvent,
  isRewardEvent,
  isRewardOfferedEvent,
  isUserActivityOnboardingState,
  getRewardIdFromEventAction,
  isAutoOmittedEvent,
  isMarkedCompletedEvent,
} from "@domain/userActivity";

import type { UserActivityAction, UserActivityData, UserActivityOnboardingData } from "@domain/userActivity";

// I18n
import { getI18nInstance } from "@locales/i18n";
import esMessages from "./i18n/onboarding.es.json";
import ptMessages from "./i18n/onboarding.pt.json";
import { useBrand } from "@/vue/api/modules/brand/brandEditor";

export const useOnboardingStore = defineStore("onboardingStore", () => {
  const userActivityApp = useUserActivityApplication();
  const routerApp = useRouterApp();

  const sendersStore = useSendersStore();
  const userStore = useUserStore();
  const sessionStore = useSessionStore();
  const contactsStore = useContactsStore();
  const accountStore = useAccountStore();
  const integrationsStore = useIntegrationsStore();
  const onboardingStore = useOnboardingStore();
  const userActivity = useUserActivityStore();
  const invoicePayment = useInvoicePaymentsStore();
  const alertStore = useAlertStore();
  const trackingEventsService = useTrackingEvents();
  const brandApi = useBrand();

  const { events, eventsFetched } = storeToRefs(userActivity);
  const { t, mergeLocaleMessage } = getI18nInstance().global;

  mergeLocaleMessage("es", esMessages);
  mergeLocaleMessage("pt", ptMessages);

  const automationIds = {
    tiendanube: {
      es: {
        abandonedCart: "auto_clcf6gv7g000b0925s183nz2h",
        visitRemarketing: "auto_clcf6gw1r001509254d93oxgy",
        pendingPayments: "auto_cm20kbe1002ce0826wi6cwuig",
      },
      pt: {
        abandonedCart: "auto_clf9q4orn002108007pa1k10z",
        visitRemarketing: "auto_clf9q6xsy002i0800zj0q3o4s",
        pendingPayments: "auto_cm20kdjg600zg0906787a9633",
      },
    },
    tray: {
      es: {},
      pt: {
        abandonedCart: "auto_clfk4lozl001j0b45vnrfy4a1",
      },
    },
    vtex: {
      es: {
        abandonedCart: "auto_clfh7u4du002e0845nqyxo36i",
        visitRemarketing: "auto_clfh7vmnp002h08454fgs2b9f",
      },
      pt: {
        abandonedCart: "auto_clfha3544002v0845584796dl",
        visitRemarketing: "auto_clfha3xm4002z08451ju16srn",
      },
    },
    jumpseller: {
      es: {
        abandonedCart: "auto_clfjra6w900030b45ri2hdm86",
      },
      pt: {
        abandonedCart: "auto_clfjt5m9d00080954bbe54ptp",
      },
    },
    wbuy: {
      es: {},
      pt: {
        abandonedCart: "auto_clftzfupe000u0a86ce6fz3yp",
      },
    },
  };

  const getAutomationId = (taskType: "abandonedCart" | "visitRemarketing" | "pendingPayments"): string | undefined => {
    const ecommerce = recommendedStore.value;
    const lang = sessionStore.session.lang === "pt" ? "pt" : "es";

    if (!ecommerce || !automationIds[ecommerce]) {
      return undefined;
    }

    return automationIds[ecommerce][lang]?.[taskType];
  };

  const rewards = reactive<Rewards>({
    trial: {
      daysToClaim: 30,
      enable: true,
      offered: false,
      claimed: false,
      async execute() {
        if (sessionStore.session.isMasterUser) return;

        console.log("apply trial");
        userActivityApp.submit({
          action: `reward.trial.claimed`,
        });
        this.claimed = true;
        alertStore.update();
      },
    },
    demo: {
      daysToClaim: 30,
      enable: true,
      offered: false,
      claimed: false,
      async execute() {
        if (sessionStore.session.isMasterUser) return;

        const esUrl = "https://calendly.com/d/dnc-y2d-3cv?hide_gdpr_banner=1";
        const ptUrl = "https://calendly.com/d/dnn-kmy-hzj?hide_gdpr_banner=1";

        window.Calendly.showPopupWidget(sessionStore.session.lang === "pt" ? ptUrl : esUrl);
        userActivityApp.submit({
          action: `reward.demo.claimed`,
        });
        this.claimed = true;
      },
    },
    discount: {
      daysToClaim: 30,
      enable: true,
      offered: false,
      claimed: false,
      async execute() {
        if (sessionStore.session.isMasterUser) return;

        console.log("show discount");
        userActivityApp.submit({
          action: `reward.discount.claimed`,
        });
        this.claimed = true;
      },
    },
    customTemplate: {
      daysToClaim: 30,
      enable: true,
      offered: false,
      claimed: false,
      async execute() {
        if (sessionStore.session.isMasterUser) return;

        openSupportPrefill(t(`reward.customTemplate.message`));
        userActivityApp.submit({
          action: `reward.customTemplate.claimed`,
        });
        this.claimed = true;
      },
    },
    workshop2500Contacts: {
      // daysToClaim: 30,
      enable: true,
      offered: false,
      claimed: false,
      async execute() {
        if (sessionStore.session.isMasterUser) return;

        window.open("https://www.google.com.ar", "_blank");
        userActivityApp.submit({
          action: `reward.workshop2500Contacts.claimed`,
        });
        this.claimed = true;
      },
    },
    workshopFewContacts: {
      // daysToClaim: 30,
      enable: true,
      offered: false,
      claimed: false,
      async execute() {
        if (sessionStore.session.isMasterUser) return;

        window.open("https://www.google.com.ar", "_blank");
        userActivityApp.submit({
          action: `reward.workshopFewContacts.claimed`,
        });
        this.claimed = true;
      },
    },
  });

  // * ----- Rewards update status with events -----
  watchEffect(() => {
    rewards.trial.enable =
      sessionStore.trialStatus === "none" &&
      (!sessionStore.plan?.type || sessionStore.plan?.type === "FREE") &&
      contactsStore.contactsCount < 500;

    rewards.discount.enable = invoicePayment.invoices.length === 0;

    if (!eventsFetched.value) {
      return;
    }

    events.value.forEach((event) => {
      if (!isRewardEvent(event.action)) return;

      const rewardId = getRewardIdFromEventAction(event.action);
      const daysToClaim = rewards[rewardId].daysToClaim;

      if (isRewardClaimedEvent(event.action)) {
        rewards[rewardId].enable = false;
        rewards[rewardId].claimed = true;
        return;
      }

      if (isRewardOfferedEvent(event.action) && daysToClaim) {
        rewards[rewardId].enable = Math.abs(differenceInDays(new Date(), new Date(event.created))) <= daysToClaim;
        rewards[rewardId].offered = true;
        return;
      }
    });
  });

  const firstTasksState = ref<Tasks>();
  const tasks = reactive<Tasks>({
    campaignSent: {
      completed: false,
      omitted: false,
      enable: true,
      action: async () => {
        routerApp.navigate({
          path: "campaigns?campaign_create=true",
        });
      },
      moreAction: () => {
        openHelpArticle(1079);
      },
    },
    importList: {
      completed: false,
      omitted: false,
      enable: true,
      action: async () => {
        routerApp.navigate({
          path: "lists/import",
        });
      },
      moreAction: () => {
        openHelpArticle(873);
      },
      omit: async () => {
        await onboardingStore.setTaskOmitted({
          id: "importList",
        });
      },
    },
    collaboration: {
      completed: false,
      omitted: false,
      enable: false,
      omit: async () => {
        await onboardingStore.setTaskOmitted({
          id: "collaboration",
        });
      },
    },
    enableWelcomeAutomation: {
      completed: false,
      omitted: false,
      enable: true,
      action: async () => {
        routerApp.navigate({
          path: "automations/all?autom_category=welcome",
        });
      },
      moreAction: () => {
        openHelpArticle(6671345);
      },
      markComplete: async () => {
        await onboardingStore.setTaskMarkedComplete({
          id: "enableWelcomeAutomation",
        });
      },
    },
    enableOptin: {
      completed: false,
      omitted: false,
      enable: true,
      action: async () => {
        routerApp.navigate({
          path: "optins/new",
        });
      },
      moreAction: () => {
        openHelpArticle(1748);
      },
      markComplete: async () => {
        await onboardingStore.setTaskMarkedComplete({
          id: "enableOptin",
        });
      },
    },
    sender: {
      completed: false,
      omitted: false,
      enable: true,
      moreAction: () => {
        openHelpArticle(1820858);
      },
    },
    storeIntegration: {
      completed: false,
      omitted: false,
      enable: true,
      omit: async () => {
        await onboardingStore.setTaskOmitted({
          id: "storeIntegration",
        });
      },
      moreAction: () => {
        routerApp.navigate({
          path: "integrations",
        });
      },
    },
    brandEditor: {
      completed: false,
      omitted: false,
      enable: true,
    },
    abandonedCart: {
      completed: false,
      omitted: false,
      enable: true,
      action: async () => {
        const automationId = getAutomationId("abandonedCart");

        if (automationId) {
          routerApp.navigate({
            path: `automations/all?autom_id=${automationId}`,
          });
        }
        if (!automationId) {
          routerApp.navigate({
            path: "automations/all?autom_category=recover",
          });
        }
      },
      moreAction: () => {
        openHelpArticle(6671345);
      },
      markComplete: async () => {
        await onboardingStore.setTaskMarkedComplete({
          id: "abandonedCart",
        });
      },
    },
    recoverInactive: {
      completed: false,
      omitted: false,
      enable: false,
      action: async () => {
        routerApp.navigate({
          path: "automations/all?autom_category=reactivation",
        });
      },
      moreAction: () => {
        openHelpArticle(6671345);
      },
      markComplete: async () => {
        await onboardingStore.setTaskMarkedComplete({
          id: "recoverInactive",
        });
      },
    },
    loyalty: {
      completed: false,
      omitted: false,
      enable: false,
      action: async () => {
        routerApp.navigate({
          path: "automations/all?autom_category=loyalty",
        });
      },
      moreAction: () => {
        openHelpArticle(6671345);
      },
      markComplete: async () => {
        await onboardingStore.setTaskMarkedComplete({
          id: "loyalty",
        });
      },
    },
    visitRemarketing: {
      completed: false,
      omitted: false,
      enable: true,
      action: async () => {
        const automationId = getAutomationId("visitRemarketing");

        if (automationId) {
          routerApp.navigate({
            path: `automations/all?autom_id=${automationId}`,
          });
        }
        if (!automationId) {
          routerApp.navigate({
            path: "automations/all?autom_category=reactivation",
          });
        }
      },
      moreAction: () => {
        openHelpArticle(6671345);
      },
      markComplete: async () => {
        await onboardingStore.setTaskMarkedComplete({
          id: "visitRemarketing",
        });
      },
    },
    pendingPayments: {
      completed: false,
      omitted: false,
      enable: true,
      action: async () => {
        const automationId = getAutomationId("pendingPayments");

        if (automationId) {
          routerApp.navigate({
            path: `automations/all?autom_id=${automationId}`,
          });
        }
        if (!automationId) {
          routerApp.navigate({
            path: "automations/all?autom_category=loyalty",
          });
        }
      },
      moreAction: () => {
        openHelpArticle(6671345);
      },
      markComplete: async () => {
        await onboardingStore.setTaskMarkedComplete({
          id: "pendingPayments",
        });
      },
    },
  });

  const ecommerceIntegration = computed<Ecommerce | undefined>(() => integrationsStore.ecommerceIntegrations[0]);
  const recommendedStore = computed<Ecommerce | undefined>(() => {
    if (ecommerceIntegration.value) return ecommerceIntegration.value;

    const accountStoreType = accountStore.getStoreType();
    if (accountStoreType && isEcommerceId(accountStoreType)) {
      return accountStoreType;
    }

    return undefined;
  });

  const { senders } = storeToRefs(sendersStore);
  const { users } = storeToRefs(userStore);

  const isFirstOnboardingState = ref(true);

  // * ----- Tasks update with data and events -----
  const loadingTasks = ref(true);
  watchEffect(async () => {
    if (
      !sendersStore.sendersFetched ||
      !accountStore.accountConfigDataFetched ||
      !userStore.usersFetched ||
      !integrationsStore.integrationsFetched ||
      !userActivity.eventsFetched
    ) {
      return;
    }
    loadingTasks.value = true;

    const someSendersIsOk = senders.value?.some((s) => s.auth.status === "OK");
    const hasSupportedRecommendedEcommerce = !!recommendedStore.value && isSupportedEcommerce(recommendedStore.value);
    const hasOptinSupport = !!recommendedStore.value && ecommerceHasOptinSupport(recommendedStore.value);
    const hasAbandonedCart = !!recommendedStore.value && ecommerceHasAbandonedCart(recommendedStore.value);
    const hasRemarketing = !!recommendedStore.value && ecommerceHasRemarketing(recommendedStore.value);
    const hasPendingPayments = !!recommendedStore.value && ecommerceHasPendingPayments(recommendedStore.value);
    const brand = await brandApi.current();

    if (!tasks.collaboration.completed) {
      tasks.collaboration.completed = !!users.value?.length && users.value.length > 1;
    }

    if (!tasks.storeIntegration.completed) {
      tasks.storeIntegration.completed = !!ecommerceIntegration.value;
    }

    if (!tasks.sender.completed) {
      tasks.sender.completed = !!someSendersIsOk;
    }

    if (!tasks.brandEditor.completed) {
      tasks.brandEditor.completed = brand != null && Object.keys(brand).length > 0;
    }

    tasks.importList.enable = !recommendedStore.value || !hasSupportedRecommendedEcommerce;

    tasks.enableOptin.enable = hasOptinSupport;

    tasks.abandonedCart.enable = hasSupportedRecommendedEcommerce && hasAbandonedCart;

    tasks.visitRemarketing.enable = hasSupportedRecommendedEcommerce && hasRemarketing;

    tasks.pendingPayments.enable = hasSupportedRecommendedEcommerce && hasPendingPayments;

    events.value.forEach((event) => {
      if (isUserActivityOnboardingState(event)) {
        firstTasksState.value = event.data.tasks;
        isFirstOnboardingState.value = false;
        return;
      }

      const actionMap: Partial<Record<UserActivityAction, TaskIds>> = {
        "automation.welcome.enabled": "enableWelcomeAutomation",
        "automation.transactional.enabled": "pendingPayments",
        "automation.abandoned_cart.enabled": "abandonedCart",
        "automation.visit_recovery.enabled": "visitRemarketing",
        "automation.loyalty.enabled": "loyalty",
        "automation.reactivation.enabled": "recoverInactive",
        "list.imported": "importList",
        "campaign.sent": "campaignSent",
        "optin.enabled": "enableOptin",
      };

      const taskIdDetected = actionMap[event.action];
      if (taskIdDetected && tasks[taskIdDetected]) {
        tasks[taskIdDetected].completed = true;
        return;
      }

      if (isAutoOmittedEvent(event.action) || isMarkedCompletedEvent(event.action)) {
        const taskId = getTaskIdFromEvent(event.action);
        if (tasks[taskId]) {
          tasks[taskId].completed = true;
        }
        return;
      }

      if (isOmittedEvent(event.action)) {
        const taskId = getTaskIdFromEvent(event.action);
        if (tasks[taskId]) {
          tasks[taskId].omitted = true;
        }
        return;
      }
    });

    loadingTasks.value = false;

    if (!isFirstOnboardingState.value) return;

    isFirstOnboardingState.value = events.value.every((event) => event.action !== "onboarding.first.state");

    if (!isFirstOnboardingState.value) return;

    // * Emits first onboarding state
    await userActivityApp.submit({
      action: "onboarding.first.state",
      data: {
        tasks,
      },
    });

    firstTasksState.value = tasks;
    isFirstOnboardingState.value = false;
  });

  const emitRewardOffered = (rewardId: RewardId) => {
    const alreadyEmmited = events.value.some((event) => {
      if (!isRewardEvent(event.action)) return false;

      return getRewardIdFromEventAction(event.action) === rewardId;
    });

    if (alreadyEmmited || sessionStore.session.isMasterUser) return;

    userActivityApp.submit({
      action: `reward.${rewardId}.offered`,
    });

    rewards[rewardId].offered = true;
  };

  const setTaskOmitted = ({ id }: { id: TaskIds }) => {
    if (tasks?.[id]?.completed) return;
    userActivityApp.submit({
      action: `onboarding.${id}.omitted`,
    });

    tasks[id] = {
      ...tasks[id],
      completed: tasks[id].completed,
      omitted: true,
      enable: tasks[id].enable,
    };

    const action = getEventActionFromTaskId(id);
    trackingEventsService.amplitude({
      name: "APP_HOME_TASK_OMITTED",
      data: {
        action: action,
        taskId: id,
      },
    });
  };

  const setTaskCompleted = async ({ id }: { id: TaskIds }) => {
    tasks[id] = {
      ...tasks[id],
      completed: true,
      omitted: tasks[id].omitted,
      enable: tasks[id].enable,
    };
  };

  const setTaskMarkedComplete = ({ id }: { id: TaskIds }) => {
    if (tasks?.[id]?.completed) return;
    userActivityApp.submit({
      action: `onboarding.${id}.markedComplete`,
    });

    tasks[id] = {
      ...tasks[id],
      completed: true,
      omitted: tasks[id].omitted,
      enable: tasks[id].enable,
    };

    const action = getEventActionFromTaskId(id);
    trackingEventsService.amplitude({
      name: "APP_HOME_TASK_MARKED_COMPLETE",
      data: {
        action: action,
        taskId: id,
      },
    });
  };

  const submitUserEvent = async (params: {
    action: UserActivityAction;
    timestamp?: DateString;
    data?: UserActivityData | UserActivityOnboardingData;
  }) => {
    await userActivityApp.submit(params);

    const taskId = getTaskIdFromEventAction(params.action);

    if (taskId) {
      trackingEventsService.amplitude({
        name: "APP_HOME_TASK_COMPLETED",
        data: {
          action: params.action,
          taskId: taskId,
        },
      });

      setTaskCompleted({ id: taskId });
    }
  };

  const tasksProgress = computed(() => {
    const enabledTasks = Object.values(tasks).filter((task) => task.enable);
    const completedTasks = enabledTasks.filter((task) => task.completed || task.omitted);

    const completed = completedTasks.length;
    const total = enabledTasks.length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

    return {
      completed,
      total,
      percentage,
    };
  });

  return {
    tasks,
    loadingTasks,
    firstTasksState,
    rewards,
    recommendedStore,
    ecommerceIntegration,
    events,
    tasksProgress,
    emitRewardOffered,
    setTaskOmitted,
    setTaskCompleted,
    setTaskMarkedComplete,
    submitUserEvent,
  };
});
